<template>
  <div>
    <!-- Summary Cards -->
    <div class="mt-8">
      <div class="bg-layer-1 rounded-xl p-6 w-full max-w-md border border-stroke-separator">
        <h2 class="text-lg font-medium mb-4">{{ $t('org_summary_title') }}</h2>

        <div class="grid grid-cols-2 gap-4">
          <div class="rounded-lg py-2 px-4 border border-stroke-separator">
            <div class="text-sm text-text-neutral-high">{{ $t('org_summary_used_title') }}</div>
            <div class="text-lg text-text-neutral-extreme font-semibold mt-1">
              {{ formatDuration(resourcesUsed) }}
            </div>
          </div>

          <div class="rounded-lg py-2 px-4 border border-stroke-separator">
            <div class="text-sm text-text-neutral-high">{{ $t('common_available') }}</div>
            <div class="text-lg text-text-neutral-extreme font-semibold mt-1">
              {{ formatDuration(resourcesRemaining) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Table Section -->
    <div class="mt-10 bg-layer-1 rounded-xl border border-stroke-separator hidden md:block">

      <!-- Search + Count -->
      <div class="flex items-center justify-between px-3 py-3 border-b border-stroke-separator">
        <!-- SEARCH -->
        <div
            class="flex items-center max-w-full overflow-hidden"
            :class="{ 'border border-stroke-resting rounded-[6px] bg-elements-clear': searchOpen }"
        >
          <!-- INPUT -->
          <div
              class="flex items-center h-10 transition-all duration-200 ease-out overflow-hidden"
              :class="searchOpen ? 'w-64 px-3 opacity-100' : 'w-0 px-0 opacity-0'"
          >
            <input
                ref="searchInput"
                v-model="search"
                type="text"
                placeholder="Search"
                class="flex-1 bg-transparent outline-none text-md
                   text-text-neutrals-placeholder
                   placeholder-text-neutrals-placeholder"
                @blur="onSearchBlur"
                @keydown.enter.prevent="searchAction"
            />

            <button
                v-if="search"
                @click="clearSearch"
                class="ml-2 text-text-neutrals-placeholder hover:text-text-neutrals-primary"
            >
              ✕
            </button>
          </div>

          <!-- SEARCH BUTTON -->
          <button
              @click="searchAction"
              class="h-10 w-10 flex items-center justify-center
                 text-text-neutrals-placeholder hover:text-text-neutrals-primary
                 shrink-0 cursor-pointer"
              :class="{ 'bg-button-secondary-mono-resting': searchOpen }"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round"
                    d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
            </svg>
          </button>
        </div>

        <!-- COUNT -->
        <div class="flex items-center space-x-2 text-sm text-text-neutral-extreme">
          <span>{{ pagination.total_items }} Members</span>
        </div>
      </div>

      <!-- Table -->
      <div class="relative w-full overflow-x-auto">
        <table class="min-w-[720px] w-full text-sm table-fixed">
          <colgroup>
            <col style="width: 30%" />
            <col style="width: 30%" />
            <col style="width: 20%" />
            <col style="width: 13%" />
<!--              <col style="width: 7%" />-->
          </colgroup>

          <thead class="bg-layer-1 border-b border-stroke-separator text-text-neutral-medium text-xs">
          <tr>
            <th class="text-left py-4 px-3 font-bold">Name</th>
            <th class="text-left py-4 px-3 font-bold">Email</th>
            <th class="text-left py-4 px-3 font-bold">Usage</th>
            <th class="text-left py-4 px-3 font-bold">Status</th>
<!--              <th class="text-center py-4 px-3 font-bold">Actions</th>-->
          </tr>
          </thead>

          <tbody class="divide-y divide-stroke-separator">
          <tr v-for="member in members" :key="member.id" class="hover:bg-gray-750">
            <td class="py-4 px-3">{{ member.name }}</td>
            <td class="py-4 px-3 whitespace-nowrap text-ellipsis overflow-hidden ">{{ member.email }}</td>
            <td class="py-4 px-3">{{ formatDuration(member.used) }}</td>
            <td class="py-4 px-3">
              <div class="flex items-center gap-1">
                <span
                    class="w-2 h-2 rounded-full"
                    :class="{
                    'bg-elements-blue': member.status === 'active',
                    'bg-elements-red': member.status === 'deleted'
                  }"
                ></span>
                <span class="capitalize">{{ member.status }}</span>
              </div>
            </td>
<!--              <td class="py-4 px-3 text-center">
              <button
                  v-if="member.status === 'active'"
                  class="text-text-neutrals-placeholder hover:text-text-neutrals-primary cursor-pointer"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M4.41074 4.41074C4.73618 4.08531 5.26382 4.08531 5.58926 4.41074L10 8.82149L14.4107 4.41074C14.7362 4.08531 15.2638 4.08531 15.5893 4.41074C15.9147 4.73618 15.9147 5.26382 15.5893 5.58926L11.1785 10L15.5893 14.4107C15.9147 14.7362 15.9147 15.2638 15.5893 15.5893C15.2638 15.9147 14.7362 15.9147 14.4107 15.5893L10 11.1785L5.58926 15.5893C5.26382 15.9147 4.73618 15.9147 4.41074 15.5893C4.08531 15.2638 4.08531 14.7362 4.41074 14.4107L8.82149 10L4.41074 5.58926C4.08531 5.26382 4.08531 4.73618 4.41074 4.41074Z" fill="white" fill-opacity="0.55"/>
                </svg>
              </button>
            </td>-->
          </tr>
          </tbody>
        </table>
      </div>

      <!-- Footer + Pagination -->
      <div class="flex items-center justify-between px-4 py-4 border-t border-stroke-separator">
        <!-- PER PAGE -->
        <div class="flex items-center gap-2">
          <Listbox v-model="pagination.items_per_page">
            <div class="relative">
              <ListboxButton
                  class="min-w-[64px] bg-elements-clear border border-stroke-separator
                     rounded-lg py-1.5 pl-2 pr-8 text-sm
                     text-text-neutrals-primary focus:outline-none"
              >
                {{ pagination.items_per_page }}
                <span class="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                <svg class="h-4 w-4 text-text-neutrals-placeholder" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.38a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
                        clip-rule="evenodd"/>
                </svg>
              </span>
              </ListboxButton>

              <ListboxOptions
                  class="absolute mt-1 w-full rounded-lg border border-stroke-separator
                     bg-elements-clear shadow-lg z-50"
              >
                <ListboxOption
                    v-for="n in perPageOptions"
                    :key="n"
                    :value="n"
                    v-slot="{ active, selected }"
                >
                  <li
                      class="cursor-pointer px-2 py-1.5 text-sm"
                      :class="[
                    active ? 'bg-layer-2' : '',
                    selected ? 'font-medium' : ''
                  ]"
                  >
                    {{ n }}
                  </li>
                </ListboxOption>
              </ListboxOptions>
            </div>
          </Listbox>

          <span class="text-text-neutrals-placeholder text-sm">per page</span>
        </div>

        <!-- PAGINATION -->
        <div class="flex items-center space-x-2">
          <button
              class="px-2 py-1 rounded-lg"
              :class="{ 'opacity-30 cursor-default': pagination.current_page === 1, 'cursor-pointer': pagination.current_page !== 1 }"
              @click="pagination.current_page > 1 && goToPage(pagination.current_page - 1)"
          >
            ‹
          </button>

          <button
              v-for="(page, i) in displayPages"
              :key="i"
              @click="page !== '...' && goToPage(page)"
              class="px-3 py-1.5 rounded-lg text-sm"
              :class="[
            page === pagination.current_page
              ? 'bg-gray-700 text-white'
              : page === '...'
                ? 'cursor-default text-gray-500'
                : 'text-gray-400 hover:bg-gray-700 hover:text-white cursor-pointer',
          ]"
              :disabled="page === '...'"
          >
            {{ page }}
          </button>

          <button
              class="px-2 py-1 rounded-lg"
              :class="{ 'opacity-30 cursor-default': pagination.current_page === pagination.total_pages, 'cursor-pointer': pagination.current_page !== pagination.total_pages }"
              @click="pagination.current_page < pagination.total_pages && goToPage(pagination.current_page + 1)"
          >
            ›
          </button>
        </div>
      </div>
    </div>

    <!-- for mobile users -->
    <div class="flex md:hidden mt-7">
      <div class="flex flex-col gap-1 pl-3 pr-9 py-3 border border-stroke-separator bg-layer-2 rounded-[10px]">
        <h4 class="text-text-neutral-extreme font-bold text-sm">{{ $t('mobile_detected_title') }}</h4>
        <span class="text-text-neutral-medium text-sm">{{ $t('mobile_detected_text')}}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue'
import api from '@/services/api2.service'
import { Pagination } from '@/types/pagination'

// STORE
const store = useStore()
const organization = computed(() => store.getters['nonpersist/getUserOrganization'])
const organizationId = computed(() => organization.value?.id)

// STATE
const loading = ref(false)
const error = ref<string | null>(null)
const orgInfo = ref<any>(null)
const members = ref<any[]>([])

const pagination = ref<Pagination>({
  total_pages: 1,
  items_per_page: 8,
  current_page: 1,
  total_items: 0
})

const perPageOptions = [8, 16, 24]

// RESOURCES
const resourcesUsed = computed(() => {
  const resources = orgInfo.value?.resources

  if (!resources?.length) return 0

  return Math.ceil(
      resources
          .reduce(
              (sum: number, r: any) => sum + (r.used_resources || 0),
              0
          )
  )
})

const resourcesAvailable = computed(() => {
  const resources = orgInfo.value?.resources

  if (!resources?.length) return 0

  return Math.ceil(
      resources
          .filter((r: any) => r.status !== 'expired')
          .reduce(
              (sum: number, r: any) => sum + (r.total_resources || 0),
              0
          )
  )
})

const resourcesUserFromActive = computed(() => {
  const resources = orgInfo.value?.resources

  if (!resources?.length) return 0

  return Math.ceil(
      resources
          .filter((r: any) => r.status !== 'expired')
          .reduce(
              (sum: number, r: any) => sum + (r.used_resources || 0),
              0
          )
  )
})

const resourcesRemaining = computed(() => {
  console.log(resourcesAvailable.value - resourcesUserFromActive.value)
  return resourcesAvailable.value - resourcesUserFromActive.value;
})


// PAGINATION UI
const displayPages = computed(() => {
  const { total_pages, current_page } = pagination.value
  if (!total_pages) return []
  const pages: (number | '...')[] = [1]
  if (current_page > 3) pages.push('...')
  for (let i = current_page - 1; i <= current_page + 1; i++) {
    if (i > 1 && i < total_pages) pages.push(i)
  }
  if (current_page < total_pages - 2) pages.push('...')
  if (total_pages > 1) pages.push(total_pages)
  return pages
})

// SEARCH
const search = ref('')
const searchOpen = ref(false)
const searchInput = ref<HTMLInputElement | null>(null)

// API
async function fetchMembers(page = pagination.value.current_page, force = false) {
  if (!force && page === pagination.value.current_page) return
  try {
    loading.value = true
    const { data } = await api.get(`/organization/users/${organizationId.value}`, {
      params: {
        page,
        per_page: pagination.value.items_per_page,
        filter_search_keyword: search.value || undefined,
        sort_by: 'used',
        sort_type: 'DESC'
      }
    })
    members.value = data?.result?.items ?? []
    pagination.value = data?.result?.pagination ?? pagination.value
  } catch (err: any) {
    error.value = err?.response?.data?.message || 'Failed to load members'
  } finally {
    loading.value = false
  }
}

function goToPage(page: number, force = false) {
  return fetchMembers(page, force)
}

function searchAction() {
  if (!searchOpen.value) {
    searchOpen.value = true
    nextTick(() => searchInput.value?.focus())
    return
  }
  pagination.value.current_page = 1
  fetchMembers(1, true)
}

function clearSearch() {
  search.value = ''
  searchOpen.value = false
  pagination.value.current_page = 1
  fetchMembers(1, true)
}

function onSearchBlur() {
  if (!search.value) searchOpen.value = false
}

function formatDuration(seconds: number): string {
  if (!seconds || seconds < 0) return '0h 0m'

  const h = Math.floor(seconds / 3600)
  let m = Math.ceil((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60);

  if (s > 0 && m <= 0) {
    m++;
  }

  return `${h}h ${m}m`
}


// WATCHERS
watch(
    () => pagination.value.items_per_page,
    async (n, o) => {
      if (n === o) return
      pagination.value.current_page = 1
      await fetchMembers(1, true)
    }
)

// INIT
onMounted(async () => {
  const { data } = await api.get(`/organization/info/${organizationId.value}`)
  orgInfo.value = data?.result ?? null
  await fetchMembers(1, true)
})
</script>
